import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import * as Yup from "yup";

import ApiErrorsHelper from "~/admin/helpers/ApiErrorsHelper";
import useLoader from "~/hooks/useLoader";
import ValidationErrors from "~/admin/services/ValidationErrors";
import Button from "~/admin/components/Button";
import Input from "~/admin/components/Input";
import Label from "~/admin/components/Label";
import Modal from "~/admin/components/Modal";
import ValidationForm from "~/admin/components/ValidationForm";
import ImageUploader from "~/admin/components/ImageUploader";
import SubmitButton from "~/admin/components/SubmitButton";
import useDataLoading from "~/admin/hooks/useDataLoading";
import PageDataLoader from "~/admin/components/PageDataLoader";
import SocialNetworksApi from "~/admin/apis/socialNetworks.api";

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .max(255, ValidationErrors.isLength(255))
    .required(ValidationErrors.required),
  color: Yup.string()
    .max(7, ValidationErrors.isLength(7))
    .required(ValidationErrors.required),
  image: Yup.mixed().when("type", {
    is: (type) => type === "image",
    then: (schema) => schema.required(ValidationErrors.required),
  }),
  logo: Yup.mixed().when("type", {
    is: (type) => type === "image",
    then: (schema) => schema.required(ValidationErrors.required),
  }),
  logoWhite: Yup.mixed().when("type", {
    is: (type) => type === "image",
    then: (schema) => schema.required(ValidationErrors.required),
  }),
});

const UpdateSocialNetworkModal = forwardRef(({ onSubmitted }, ref) => {
  const [isOpened, setIsOpened] = useState(false);
  const [socialNetwork, setSocialNetwork] = useState(null);

  const form = useRef();
  const loader = useLoader();
  const dataLoading = useDataLoading();

  useImperativeHandle(ref, () => ({
    open(socialNetworkId) {
      loadData(socialNetworkId);
      setIsOpened(true);
    },
  }));

  const loadData = async (socialNetworkId) => {
    dataLoading.start();

    let error = null;

    let socialNetwork = await SocialNetworksApi.get(socialNetworkId).catch(
      (e) => {
        error = e;
        return null;
      }
    );

    if (socialNetwork) {
      setSocialNetwork(socialNetwork);
    }

    dataLoading.stop(error);
  };

  const close = () => {
    setIsOpened(false);
  };

  const onSubmit = async (formData) => {
    loader.show();

    let error = null;
    await SocialNetworksApi.update(socialNetwork.id, formData, true).catch(
      (e) => {
        error = e;
        return null;
      }
    );

    loader.hide();

    if (error) {
      ApiErrorsHelper.manage(error, form);
    } else {
      setIsOpened(false);
      onSubmitted();
    }
  };

  return (
    <Modal visible={isOpened} toggle={close} size="lg">
      <Modal.Header toggle={close}>Modifier un réseau social</Modal.Header>
      <>
        <PageDataLoader isLoading={dataLoading.isLoading}>
          <ValidationForm
            ref={form}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            initialValues={{
              name: socialNetwork?.name,
              color: socialNetwork?.color,
              image: null,
              logo: null,
              logoWhite: null,
            }}
          >
            <Modal.Body>
              <Label html="name" required>
                Nom
              </Label>
              <Input id="name" name="name" />

              <Label html="color" required>
                Couleur
              </Label>
              <Input id="color" name="color" maxLength="7" />

              <Label htmlFor="image">Image</Label>
              <ImageUploader
                name="image"
                actualUrl={socialNetwork?.image}
                required
              />
              <Label htmlFor="logo">Logo</Label>
              <ImageUploader
                name="logo"
                actualUrl={socialNetwork?.logo}
                required
              />
              <Label htmlFor="logoWhite">Logo blanc</Label>
              <ImageUploader
                name="logoWhite"
                actualUrl={socialNetwork?.logoWhite}
                required
              />
            </Modal.Body>
            <Modal.Footer>
              <Button color="light" size="s" onClick={close}>
                Annuler
              </Button>
              <SubmitButton size="s" color="tertiary">
                Valider
              </SubmitButton>
            </Modal.Footer>
          </ValidationForm>
        </PageDataLoader>
      </>
    </Modal>
  );
});

export default UpdateSocialNetworkModal;
